import { HttpError } from '@littlebird/auth-client';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  toJS,
} from 'mobx';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { littlebirdSdk } from '@/lib/sdk-init';
import { saveGeneratedFile } from '@/utils/file-io';

import type {
  ChatCostSummary,
  ChatMessage,
  ChatTurnTrace,
  DebugPayloadSkeleton,
  DebugPayloadSkeletonsResponse,
  TraceSpan,
} from './chat-debug.types';

const LIVE_TRACE_REFRESH_INTERVAL_MS = 2000;

type TraceFetchOptions = {
  includeTraceData?: boolean;
  silent?: boolean;
};

export class ChatDebugSession {
  isOpen: boolean = false;
  activeMessageId: string | null = null;
  _chatTurnTraces: ChatTurnTrace[] = [];
  traceTree: TraceSpan | null = null;
  loadingTracesForChat: boolean = false;
  loadingTraceForMessage: boolean = false;
  exportingDebugData: boolean = false;
  chatCostSummary: ChatCostSummary | null = null;
  payloadSkeletons: DebugPayloadSkeletonsResponse | null = null;
  costsError: string | null = null;
  payloadsError: string | null = null;
  loadingCosts: boolean = false;
  sectionExpansion = observable.map<string, boolean>();

  private tracesForChatController: AbortController | null = null;
  private traceForMessageController: AbortController | null = null;
  private costsController: AbortController | null = null;

  constructor(readonly chatId: string) {
    makeObservable(this, {
      isOpen: observable,
      activeMessageId: observable,
      _chatTurnTraces: observable,
      traceTree: observable,
      loadingTracesForChat: observable,
      loadingTraceForMessage: observable,
      exportingDebugData: observable,
      chatCostSummary: observable,
      payloadSkeletons: observable,
      costsError: observable,
      payloadsError: observable,
      loadingCosts: observable,
      sectionExpansion: observable,
      messages: computed,
      _activeTurn: computed,
      activeRequestPayloads: computed,
      activeMessageResponses: computed,
      activeTraceAvailable: computed,
      activeTraceId: computed,
      setIsOpen: action,
      setActiveMessageId: action,
      setSectionExpanded: action,
      setTracesForChatResult: action,
      setTraceForMessageResult: action,
      fetchTracesForChat: action,
      fetchTraceForMessage: action,
      exportDebugData: action,
      fetchDebugData: action,
      ensureDebugData: action,
    });
  }

  setIsOpen(isOpen: boolean) {
    this.isOpen = isOpen;
    if (isOpen) {
      this.ensureDebugData();
    }
  }

  setActiveMessageId(messageId: string | null) {
    this.activeMessageId = messageId;
  }

  isSectionExpanded(sectionKey: string, defaultExpanded: boolean): boolean {
    return this.sectionExpansion.get(sectionKey) ?? defaultExpanded;
  }

  setSectionExpanded(sectionKey: string, isExpanded: boolean) {
    this.sectionExpansion.set(sectionKey, isExpanded);
  }

  private getTurnId(turn: ChatTurnTrace, index: number): string {
    const firstUserMessage = turn.messages.find(
      (message) => message.role === 'user'
    );
    return firstUserMessage?.id ?? turn.trace_id ?? `turn-${index}`;
  }

  get messages() {
    return this._chatTurnTraces.map((turn, index) => {
      const firstUserMessage = turn.messages.find(
        (message) => message.role === 'user'
      );
      const id = this.getTurnId(turn, index);
      return {
        id,
        text: turn.user_query ?? '',
        agent: firstUserMessage?.agent,
        traceId: turn.trace_id,
        trace_url: turn.trace_url,
        createdAt: firstUserMessage?.created_at
          ? new Date(firstUserMessage.created_at)
          : new Date(),
      };
    });
  }

  get _activeTurn(): ChatTurnTrace | undefined {
    if (!this.activeMessageId) return undefined;
    return this._chatTurnTraces.find(
      (turn, index) => this.getTurnId(turn, index) === this.activeMessageId
    );
  }

  get activeMessageResponses(): ChatMessage[] {
    return this._activeTurn?.messages ?? [];
  }

  get activeRequestPayloads(): DebugPayloadSkeleton[] {
    const messageId = this._activeTurn?.messages.find(
      (message) => message.role === 'user'
    )?.id;
    if (!messageId) return [];
    return (this.payloadSkeletons?.payloads ?? [])
      .filter((payload) => payload.message_id === messageId)
      .sort(
        (first, second) =>
          (first.step_index ?? Number.MAX_SAFE_INTEGER) -
          (second.step_index ?? Number.MAX_SAFE_INTEGER)
      );
  }

  get activeTraceAvailable(): boolean {
    const turn = this._activeTurn;
    return turn?.trace_id !== null && turn?.trace_id !== undefined;
  }

  get activeTraceId(): string | null {
    return this._activeTurn?.trace_id ?? null;
  }

  private getFirstMessageId(): string | null {
    if (this._chatTurnTraces.length === 0) return null;
    return this.getTurnId(this._chatTurnTraces[0], 0);
  }

  setTracesForChatResult(
    traces: ChatTurnTrace[],
    preserveTraceData: boolean = false
  ) {
    const existingTurns = new Map(
      this._chatTurnTraces.map((turn, index) => [
        this.getTurnId(turn, index),
        turn,
      ])
    );
    if (preserveTraceData) {
      traces = traces.map((turn, index) => {
        const existing = existingTurns.get(this.getTurnId(turn, index));
        return existing && !turn.trace_id
          ? {
              ...turn,
              trace_id: existing.trace_id,
              trace_url: existing.trace_url,
            }
          : turn;
      });
    }
    this._chatTurnTraces = traces.sort((firstTrace, secondTrace) => {
      const firstTime = firstTrace.messages[0]?.created_at;
      const secondTime = secondTrace.messages[0]?.created_at;
      if (!firstTime || !secondTime) return 0;
      return new Date(firstTime).getTime() - new Date(secondTime).getTime();
    });
    this.loadingTracesForChat = false;
  }

  setTraceForMessageResult(traceTree: TraceSpan) {
    this.traceTree = traceTree;
    this.loadingTraceForMessage = false;
  }

  /** Fetches cost summary and payload skeletons for this chat. */
  ensureDebugData() {
    if (this.chatCostSummary || this.loadingCosts) return;
    // No ChatWithContext row exists until the first turn finishes.
    if (littlebirdSdk.stores.threads.isThreadFinished(this.chatId) !== true) {
      return;
    }
    void this.fetchDebugData();
  }

  startLiveTraceRefresh(): () => void {
    const refresh = () => {
      if (this.loadingTracesForChat) return;
      void this.fetchTracesForChat({ includeTraceData: false, silent: true });
    };
    refresh();
    const interval = window.setInterval(
      refresh,
      LIVE_TRACE_REFRESH_INTERVAL_MS
    );
    return () => {
      window.clearInterval(interval);
      this.tracesForChatController?.abort();
    };
  }

  async fetchTracesForChat(options: TraceFetchOptions = {}) {
    const { includeTraceData = true, silent = false } = options;
    this.tracesForChatController?.abort();
    this.tracesForChatController = new AbortController();
    const { signal } = this.tracesForChatController;

    this.loadingTracesForChat = true;
    try {
      const [traces, payloadSkeletons] = await Promise.all([
        backendApis.debug.getTracesForChat(
          this.chatId,
          signal,
          includeTraceData
        ),
        includeTraceData
          ? null
          : backendApis.debug
              .getDebugPayloadSkeletons(this.chatId, signal, false)
              .catch((error) => {
                if (error instanceof Error && error.name === 'CanceledError') {
                  throw error;
                }
                return null;
              }),
      ]);
      runInAction(() => {
        this.setTracesForChatResult(traces, !includeTraceData);
        if (payloadSkeletons) {
          this.payloadSkeletons = payloadSkeletons;
        }
        const activeMessageStillExists = this.messages.some(
          (message) => message.id === this.activeMessageId
        );
        this.activeMessageId = activeMessageStillExists
          ? this.activeMessageId
          : this.getFirstMessageId();
      });

      if (includeTraceData && this.activeTraceAvailable && this.activeTraceId) {
        void this.fetchTraceForMessage(this.activeTraceId);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'CanceledError') return;
      if (silent) return;
      console.error('Failed to fetch traces for chat:', error);
      toast.error('Failed to load traces for chat');
    } finally {
      if (this.tracesForChatController?.signal === signal) {
        this.tracesForChatController = null;
        runInAction(() => {
          this.loadingTracesForChat = false;
        });
      }
    }
  }

  async fetchTraceForMessage(traceId: string) {
    this.traceForMessageController?.abort();
    this.traceForMessageController = new AbortController();
    const { signal } = this.traceForMessageController;

    this.loadingTraceForMessage = true;
    try {
      const traceTree = await backendApis.debug.getTraceTreeForMessage(
        traceId,
        signal
      );
      if (this.activeTraceId === traceId) {
        runInAction(() => this.setTraceForMessageResult(traceTree));
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'CanceledError') return;
      console.error('Failed to fetch trace for message:', error);
      if (this.activeTraceId === traceId) {
        runInAction(() => {
          this.traceTree = null;
          this.loadingTraceForMessage = false;
        });
      }
      toast.error('Failed to load trace details');
    } finally {
      if (this.traceForMessageController?.signal === signal) {
        this.traceForMessageController = null;
      }
    }
  }

  /**
   * Refreshes, then downloads a JSON of every message's chain of thoughts,
   * call traces, and payload skeletons for this chat. Excludes LLM cost data.
   */
  async exportDebugData() {
    if (this.exportingDebugData) return;
    runInAction(() => {
      this.exportingDebugData = true;
    });

    try {
      await saveGeneratedFile(
        `chat-debug-${this.chatId}-${new Date()
          .toISOString()
          .replace(/[:.]/g, '-')}.json`,
        () => this.buildDebugExportBlob()
      );
    } catch (error) {
      console.error('Failed to export debug data:', error);
      toast.error('Failed to export debug data');
    } finally {
      runInAction(() => {
        this.exportingDebugData = false;
      });
    }
  }

  private async buildDebugExportBlob(): Promise<Blob> {
    // Refresh first so the export reflects the latest data.
    await this.fetchTracesForChat();

    // No bulk trace endpoint exists: fan out one /trace/{traceId} call per
    // turn that has a trace, leaving the selected-message view untouched.
    const turnsWithTrace = this._chatTurnTraces.filter(
      (turn): turn is ChatTurnTrace & { trace_id: string } =>
        turn.trace_id !== null
    );
    const traceResults = await Promise.allSettled(
      turnsWithTrace.map((turn) =>
        backendApis.debug.getTraceTreeForMessage(turn.trace_id)
      )
    );
    const callTraces: Record<string, TraceSpan> = {};
    traceResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        callTraces[turnsWithTrace[index].trace_id] = result.value;
      } else {
        console.error(
          'Failed to fetch trace for export:',
          turnsWithTrace[index].trace_id,
          result.reason
        );
      }
    });

    // A not-yet-persisted chat 404s here, same as fetchDebugData.
    let payloadSkeletons: DebugPayloadSkeletonsResponse | null = null;
    try {
      payloadSkeletons = await backendApis.debug.getDebugPayloadSkeletons(
        this.chatId
      );
    } catch (error) {
      console.error('Failed to fetch payload skeletons for export:', error);
    }

    const exportData = {
      chat_id: this.chatId,
      exported_at: new Date().toISOString(),
      chat_turn_traces: toJS(this._chatTurnTraces),
      call_traces: callTraces,
      payload_skeletons: payloadSkeletons,
    };

    return new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
  }

  async fetchDebugData() {
    this.costsController?.abort();
    this.costsController = new AbortController();
    const { signal } = this.costsController;

    this.loadingCosts = true;
    try {
      await Promise.all([this.fetchCosts(signal), this.fetchPayloads(signal)]);
    } finally {
      if (this.costsController?.signal === signal) {
        runInAction(() => {
          this.loadingCosts = false;
        });
        this.costsController = null;
      }
    }
  }

  private async fetchCosts(signal: AbortSignal) {
    try {
      const costs = await backendApis.debug.getChatCosts(this.chatId, signal);
      if (signal.aborted) return;
      runInAction(() => {
        this.chatCostSummary = costs;
        this.costsError = null;
      });
    } catch (error) {
      if (signal.aborted) return;
      const message = this.debugReadError(error, 'costs');
      runInAction(() => {
        this.chatCostSummary = null;
        this.costsError = message;
      });
    }
  }

  private async fetchPayloads(signal: AbortSignal) {
    try {
      const payloads = await backendApis.debug.getDebugPayloadSkeletons(
        this.chatId,
        signal
      );
      if (signal.aborted) return;
      runInAction(() => {
        this.payloadSkeletons = payloads;
        this.payloadsError = null;
      });
    } catch (error) {
      if (signal.aborted) return;
      const message = this.debugReadError(error, 'payloads');
      runInAction(() => {
        this.payloadSkeletons = null;
        this.payloadsError = message;
      });
    }
  }

  private debugReadError(
    error: unknown,
    kind: 'costs' | 'payloads'
  ): string | null {
    if (error instanceof Error && error.name === 'CanceledError') return null;
    if (error instanceof HttpError) {
      if (error.status === 413) {
        return kind === 'costs'
          ? "Cost data exceeds the debugger's size or row limits."
          : "Payload details exceed the debugger's size or row limits.";
      }
      if (
        error.status === 404 &&
        littlebirdSdk.stores.threads.isThreadFinished(this.chatId) !== true
      ) {
        return null;
      }
    }
    const message =
      kind === 'costs'
        ? 'Failed to load LLM cost data'
        : 'Failed to load payload details';
    console.error(message, error);
    toast.error(message);
    return message;
  }
}

export class ChatDebugStore {
  sidebarWidth: number = 460;
  activeChatId: string | null = null;
  private sessions = observable.map<string, ChatDebugSession>();

  constructor() {
    makeObservable(this, {
      sidebarWidth: observable,
      activeChatId: observable,
      active: computed,
      setSidebarWidth: action,
      setActiveChatId: action,
      clearActiveChatId: action,
    });
  }

  get active(): ChatDebugSession | null {
    if (!this.activeChatId) return null;
    return this.sessions.get(this.activeChatId) ?? null;
  }

  private getOrCreateSession(chatId: string): ChatDebugSession {
    if (!this.sessions.has(chatId)) {
      this.sessions.set(chatId, new ChatDebugSession(chatId));
    }
    return this.sessions.get(chatId)!;
  }

  setActiveChatId(chatId: string | null) {
    this.activeChatId = chatId;
    if (chatId) {
      this.getOrCreateSession(chatId);
    }
  }

  clearActiveChatId(chatId: string) {
    if (this.activeChatId !== chatId) return;
    this.activeChatId = null;
  }

  setSidebarWidth(width: number) {
    // Cap at 60% of viewport width to avoid crushing the main content area
    const maxWidth = Math.min(800, Math.floor(window.innerWidth * 0.6));
    this.sidebarWidth = Math.max(320, Math.min(maxWidth, width));
  }
}

export const chatDebugStore = new ChatDebugStore();
